import React, { useEffect, useState } from "react";
import { db } from "../../../../../firebase-config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../../../../../provider/Auth.provider";
import ButtonComponent from "../../../../../components/CssComponents/ButtonComponent/ButtonComponent";
import PayInstallment from "../PayInstallment/PayInstallment";
import { findUserByUID } from "../../../../../service/findUsers/findUserByUID";
import { notification } from "antd";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import {
  FaBoxOpen,
  FaGraduationCap,
  FaBook,
  FaRegFileAlt,
  FaRupeeSign,
  FaRegCalendarAlt,
  FaCheckCircle,
  FaHourglassHalf,
} from "react-icons/fa";

/* --------- gold gradient icon (no glow) ---------- */
const GoldIcon = ({ Icon, size = 18, className = "" }) => (
  <Icon
    size={size}
    className={`text-transparent bg-clip-text bg-gradient-to-tr from-yellow-300 via-amber-400 to-yellow-600 ${className}`}
    aria-hidden="true"
  />
);

/* --------- progress donut using SVG (black + gold) ---------- */
const ProgressDonut = ({ percent = 0, size = 28, stroke = 3, title = "" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={title}
    >
      <defs>
        <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FACC15" />
          <stop offset="100%" stopColor="#D4A10A" />
        </linearGradient>
      </defs>
      {/* track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#1a1a1a"
        strokeWidth={stroke}
      />
      {/* progress */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

const Pill = ({ tone = "neutral", icon, children }) => {
  const tones = {
    success:
      "text-emerald-200 border-emerald-500/20 bg-gradient-to-b from-emerald-900/50 to-emerald-900/30",
    warning:
      "text-amber-200 border-amber-500/20 bg-gradient-to-b from-amber-900/50 to-amber-900/30",
    neutral:
      "text-zinc-200 border-zinc-500/20 bg-gradient-to-b from-zinc-900/60 to-zinc-900/40",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}
    >
      {icon ? <span className="inline-flex">{icon}</span> : null}
      {children}
    </span>
  );
};

const Progress = ({ data }) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [buyItem, setBuyItem] = useState(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [buyItemIdsArray, setBuyItemIdsArray] = useState([]);

  const [expandedPackages, setExpandedPackages] = useState({});
  const [expandedBatches, setExpandedBatches] = useState({});

  useEffect(() => {
    const fetchBuyItemIdsArray = async () => {
      if (!user?.uid) {
        // notification.error({
        //   message: "User UID not found",
        //   description: "Please contact admin",
        //   placement: "topRight",
        //   duration: 3,
        // });
        return;
      }
      try {
        const userData = await findUserByUID(user.uid);
        setBuyItemIdsArray(userData?.buyItemIdsArray || []);
      } catch (error) {
        console.error("Error fetching user data:", error);
        notification.error({
          message: "Error fetching user data",
          description: "Please try again or contact admin",
          placement: "topRight",
          duration: 3,
        });
      }
    };
    fetchBuyItemIdsArray();
  }, [user?.uid]);

  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (!Array.isArray(data) || data.length === 0) {
        setPackages([]);
        return;
      }

      try {
        const batchCollection = collection(db, "lms_batch");

        const updatedPackages = await Promise.all(
          data.map(async (pkg) => {
            const batchIds = Array.isArray(pkg.packageContainingBatches)
              ? pkg.packageContainingBatches
              : [];

            const purchasedIds = Array.isArray(buyItemIdsArray)
              ? buyItemIdsArray
                  .filter(
                    (item) =>
                      item && typeof item === "object" && "buyItemId" in item
                  )
                  .map((item) => String(item.buyItemId))
              : [];

            const filteredBatchIds = batchIds.filter((batchId) =>
              purchasedIds.includes(String(batchId))
            );

            if (filteredBatchIds.length === 0) {
              return { ...pkg, batches: [] };
            }

            const batchQuery = query(
              batchCollection,
              where("batchId", "in", filteredBatchIds)
            );
            const batchSnapshot = await getDocs(batchQuery);
            const batches = batchSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            return { ...pkg, batches };
          })
        );

        setPackages(updatedPackages);
      } catch (error) {
        console.error("Error fetching batch details:", error);
        setPackages([]);
      }
    };

    fetchBatchDetails();
  }, [data, buyItemIdsArray]);

  const handleBuyClick = (item) => {
    setBuyItem(item);
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setBuyItem(null);
  };

  /* ---- payment status helpers ---- */
  const getModulePaymentStatus = (settingId) => {
    const rec = buyItemIdsArray.find(
      (item) => item?.buyItemId === settingId && item?.buyItemType === "module"
    );
    if (!rec) return "none";
    if (rec.status === "approved") return "approved";
    if (rec.status === "pending") return "pending";
    return rec.status || "none";
  };

  /* ---- progress helpers (approved only) ---- */
  const getCourseStats = (batch, courseId) => {
    const entries = Object.entries(batch?.moduleSettings || {}).filter(
      ([, s]) => s?.courseId === courseId
    );
    const total = entries.length;
    const approved = entries.reduce(
      (acc, [settingId]) =>
        getModulePaymentStatus(settingId) === "approved" ? acc + 1 : acc,
      0
    );
    return {
      approved,
      total,
      percent: total ? Math.round((approved / total) * 100) : 0,
    };
  };

  const getBatchStats = (batch) => {
    const entries = Object.entries(batch?.moduleSettings || {}).filter(
      ([_, s]) => (batch?.batchContainingCourses || []).includes(s?.courseId)
    );
    const total = entries.length;
    const approved = entries.reduce(
      (acc, [settingId]) =>
        getModulePaymentStatus(settingId) === "approved" ? acc + 1 : acc,
      0
    );
    return {
      approved,
      total,
      percent: total ? Math.round((approved / total) * 100) : 0,
    };
  };

  const getPackageStats = (pkg) => {
    let approved = 0;
    let total = 0;
    (pkg?.batches || []).forEach((b) => {
      const st = getBatchStats(b);
      approved += st.approved;
      total += st.total;
    });
    return {
      approved,
      total,
      percent: total ? Math.round((approved / total) * 100) : 0,
    };
  };

  /* ---- expand handlers ---- */
  const togglePackage = (packageIndex) => {
    setExpandedPackages((prev) => ({
      ...prev,
      [packageIndex]: !prev[packageIndex],
    }));
  };
  const toggleBatch = (packageIndex, batchIndex) => {
    const key = `${packageIndex}-${batchIndex}`;
    setExpandedBatches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="w-full text-zinc-100">
      {packages.length === 0 ? (
        <p className="text-center text-zinc-400 text-base">
          No packages found.
        </p>
      ) : (
        <div className="space-y-4">
          {packages.map((pkg, pkgIndex) => {
            const pkgStats = getPackageStats(pkg);

            return (
              <section
                key={pkgIndex}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-neutral-950 to-black shadow-[0_20px_60px_rgba(0,0,0,0.45)] overflow-hidden"
              >
                {/* Package Header */}
                <header
                  onClick={() => togglePackage(pkgIndex)}
                  role="button"
                  className="flex items-center justify-between px-4 py-3 border-b border-white/10 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-black/40 text-zinc-400">
                      {expandedPackages[pkgIndex] ? (
                        <IoChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <IoChevronForward className="h-3.5 w-3.5" />
                      )}
                    </span>

                    {/* progress donut */}
                    <div className="relative">
                      <ProgressDonut
                        percent={pkgStats.percent}
                        size={26}
                        stroke={3}
                        title="Package progress"
                      />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <FaBoxOpen className="h-3.5 w-3.5 text-transparent bg-clip-text bg-gradient-to-tr from-yellow-300 via-amber-400 to-yellow-600" />
                      </span>
                    </div>

                    <div className="grid gap-0.5">
                      <h3 className="text-[15px] font-semibold tracking-tight">
                        {pkg.packageName || "Unnamed Package"}
                      </h3>
                      <div className="text-xs text-zinc-400">
                        {pkg.batches?.length || 0}{" "}
                        {pkg.batches?.length === 1 ? "batch" : "batches"} ·{" "}
                        {pkgStats.percent}%
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-zinc-400">
                    Click to{" "}
                    {expandedPackages[pkgIndex] ? "collapse" : "expand"}
                  </div>
                </header>

                {/* Package Content */}
                {expandedPackages[pkgIndex] && (
                  <div className="p-3">
                    {pkg.batches && pkg.batches.length > 0 ? (
                      <div className="space-y-3">
                        {pkg.batches.map((batch, batchIndex) => {
                          const key = `${pkgIndex}-${batchIndex}`;
                          const bStats = getBatchStats(batch);

                          return (
                            <div
                              key={batchIndex}
                              className="rounded-xl border border-white/10 bg-black/40 overflow-hidden"
                            >
                              {/* Batch Header */}
                              <header
                                onClick={() =>
                                  toggleBatch(pkgIndex, batchIndex)
                                }
                                role="button"
                                className="flex items-center justify-between px-3 py-2.5 border-b border-white/10 hover:bg-white/5 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-black/40 text-zinc-400">
                                    {expandedBatches[key] ? (
                                      <IoChevronDown className="h-3.5 w-3.5" />
                                    ) : (
                                      <IoChevronForward className="h-3.5 w-3.5" />
                                    )}
                                  </span>

                                  {/* batch progress donut */}
                                  <div className="relative">
                                    <ProgressDonut
                                      percent={bStats.percent}
                                      size={24}
                                      stroke={3}
                                      title="Batch progress"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center">
                                      <FaGraduationCap className="h-3 w-3 text-transparent bg-clip-text bg-gradient-to-tr from-yellow-300 via-amber-400 to-yellow-600" />
                                    </span>
                                  </div>

                                  <div className="grid gap-0.5">
                                    <h4 className="text-[14px] font-semibold tracking-tight">
                                      {batch.batchName || "Unnamed Batch"}
                                    </h4>
                                    <div className="text-xs text-zinc-400">
                                      {batch.batchContainingCourses?.length ||
                                        0}{" "}
                                      {batch.batchContainingCourses?.length ===
                                      1
                                        ? "course"
                                        : "courses"}{" "}
                                      · {bStats.percent}%
                                    </div>
                                  </div>
                                </div>

                                <div className="text-xs text-zinc-400">
                                  Click to{" "}
                                  {expandedBatches[key] ? "collapse" : "expand"}
                                </div>
                              </header>

                              {/* Batch Content */}
                              {expandedBatches[key] && (
                                <div className="p-3">
                                  {batch.batchContainingCourses &&
                                  batch.batchContainingCourses.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                      {batch.batchContainingCourses.map(
                                        (courseId, courseIndex) => {
                                          const cStats = getCourseStats(
                                            batch,
                                            courseId
                                          );

                                          return (
                                            <div
                                              key={courseIndex}
                                              className="rounded-xl border border-white/10 bg-gradient-to-b from-black/60 to-neutral-950 p-3 transition-transform hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(0,0,0,0.45)]"
                                            >
                                              <div className="flex items-center gap-2 pb-2 border-b border-dashed border-white/10">
                                                {/* course donut */}
                                                <div className="relative">
                                                  <ProgressDonut
                                                    percent={cStats.percent}
                                                    size={22}
                                                    stroke={3}
                                                    title="Course progress"
                                                  />
                                                  <span className="absolute inset-0 flex items-center justify-center">
                                                    <FaBook className="h-3 w-3 text-transparent bg-clip-text bg-gradient-to-tr from-yellow-300 via-amber-400 to-yellow-600" />
                                                  </span>
                                                </div>
                                                <h5 className="text-[13px] font-semibold text-zinc-100">
                                                  Course ID: {courseId}
                                                </h5>
                                                <span className="ml-auto text-[11px] text-zinc-400">
                                                  {cStats.percent}%
                                                </span>
                                              </div>

                                              {batch.moduleSettings ? (
                                                <div className="mt-2 space-y-2">
                                                  {Object.entries(
                                                    batch.moduleSettings
                                                  )
                                                    .filter(
                                                      ([_, s]) =>
                                                        s.courseId === courseId
                                                    )
                                                    .map(
                                                      ([
                                                        settingId,
                                                        settings,
                                                      ]) => {
                                                        const paymentStatus =
                                                          getModulePaymentStatus(
                                                            settingId
                                                          );

                                                        const accent =
                                                          paymentStatus ===
                                                          "approved"
                                                            ? "from-emerald-400 to-emerald-600"
                                                            : paymentStatus ===
                                                              "pending"
                                                            ? "from-amber-300 to-amber-600"
                                                            : "from-zinc-500/70 to-zinc-700/70";

                                                        return (
                                                          <div
                                                            key={settingId}
                                                            className="relative rounded-lg border border-white/10 bg-gradient-to-b from-neutral-950 to-black p-3 pl-4 flex items-start justify-between gap-3"
                                                          >
                                                            {/* left status bar */}
                                                            <span
                                                              className={`absolute left-1.5 top-1.5 bottom-1.5 w-1 rounded-full bg-gradient-to-b ${accent}`}
                                                            />

                                                            <div className="grid gap-1 pl-2">
                                                              <div className="flex items-center gap-2">
                                                                <GoldIcon
                                                                  Icon={
                                                                    FaRegFileAlt
                                                                  }
                                                                  size={12}
                                                                />
                                                                <span className="text-[13px] font-semibold">
                                                                  {settings.moduleName ||
                                                                    "N/A"}
                                                                </span>
                                                              </div>

                                                              <div className="flex flex-wrap items-center gap-3 text-[12px] text-zinc-300">
                                                                {settings.moduleInstallmentPrice >
                                                                  0 && (
                                                                  <span className="inline-flex items-center gap-1.5">
                                                                    <GoldIcon
                                                                      Icon={
                                                                        FaRupeeSign
                                                                      }
                                                                      size={11}
                                                                    />
                                                                    ₹
                                                                    {
                                                                      settings.moduleInstallmentPrice
                                                                    }
                                                                  </span>
                                                                )}
                                                                {settings.moduleInstallmentDate && (
                                                                  <span className="inline-flex items-center gap-1.5">
                                                                    <GoldIcon
                                                                      Icon={
                                                                        FaRegCalendarAlt
                                                                      }
                                                                      size={11}
                                                                    />
                                                                    {new Date(
                                                                      settings.moduleInstallmentDate
                                                                    ).toLocaleDateString()}
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>

                                                            <div className="grid place-items-end gap-1">
                                                              {paymentStatus ===
                                                                "approved" && (
                                                                <Pill
                                                                  tone="success"
                                                                  icon={
                                                                    <GoldIcon
                                                                      Icon={
                                                                        FaCheckCircle
                                                                      }
                                                                      size={11}
                                                                    />
                                                                  }
                                                                >
                                                                  Paid
                                                                </Pill>
                                                              )}

                                                              {paymentStatus ===
                                                                "pending" && (
                                                                <Pill
                                                                  tone="warning"
                                                                  icon={
                                                                    <GoldIcon
                                                                      Icon={
                                                                        FaHourglassHalf
                                                                      }
                                                                      size={11}
                                                                    />
                                                                  }
                                                                >
                                                                  Awaiting
                                                                  approval
                                                                </Pill>
                                                              )}

                                                              {paymentStatus ===
                                                                "none" &&
                                                                settings.moduleInstallmentStatus && (
                                                                  <ButtonComponent
                                                                    onClick={() =>
                                                                      handleBuyClick(
                                                                        {
                                                                          itemPrice:
                                                                            settings.moduleInstallmentPrice,
                                                                          itemName:
                                                                            settings.moduleName,
                                                                          itemId:
                                                                            settingId,
                                                                          itemBanner:
                                                                            "https://logo.gklite.in/anuragaffection.jpg",
                                                                          itemType:
                                                                            "module",
                                                                        }
                                                                      )
                                                                    }
                                                                    buttonText="PAY NOW"
                                                                  />
                                                                )}
                                                            </div>
                                                          </div>
                                                        );
                                                      }
                                                    )}
                                                </div>
                                              ) : (
                                                <p className="mt-2 text-sm text-zinc-400">
                                                  No modules found for this
                                                  course.
                                                </p>
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-zinc-400">
                                      No courses found for this batch.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="px-3 pb-3 text-sm text-zinc-400">
                        No batches found for this package.
                      </p>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {buyItem && (
        <PayInstallment
          isVisible={isDrawerVisible}
          onClose={closeDrawer}
          buyItemPrice={buyItem.itemPrice}
          buyItemName={buyItem.itemName}
          buyItemId={buyItem.itemId}
          buyItemBanner={buyItem.itemBanner}
          buyItemType={buyItem.itemType}
          uid={user?.uid}
        />
      )}
    </div>
  );
};

export default Progress;
